import React from 'react';
import { motion } from 'framer-motion';
import { Leaf, Recycle, Sun, Droplets, FileCheck } from 'lucide-react';

const initiatives = [
  {
    icon: FileCheck,
    title: "Traceability",
    description: "Certified and audited for Organic garments (GOTS) as well as Oekotex. We ensure complete transparency in the entire supply chain from farm to finished garment.",
  },
  {
    icon: Sun,
    title: "Reduction of Emissions",
    description: "Converting energy use from Diesel to Piped Gas and Electricity. Diesel consumption dropped by over 65% from 2020-21 to 2021-22. Solar panels planned for installation.",
  },
  {
    icon: Droplets,
    title: "Effluent Treatment",
    description: "Well-established waste management policy with in-house Effluent Treatment Plant that recycles wastewater. Sludge disposed through certified waste management companies.",
  },
  {
    icon: Recycle,
    title: "Sustainable Sourcing",
    description: "Better Cotton Initiative (BCI) member promoting sustainable cotton farming. Organic and recycled fabric options available for eco-conscious brands.",
  },
];

const stats = [
  { value: "65%", label: "Diesel Reduction" },
  { value: "100%", label: "Water Recycled" },
  { value: "GOTS", label: "Certified Organic" },
  { value: "Higg", label: "Index Registered" },
];

const SustainabilitySection = () => {
  return (
    <section 
      id="sustainability"
      data-testid="sustainability-section"
      className="py-24 md:py-32 bg-white relative overflow-hidden"
    >
      {/* Background Texture */}
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-[0.02]" />

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Left - Image */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1770122985572-ca890ef5ecf3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxzdXN0YWluYWJsZSUyMG9yZ2FuaWMlMjBjb3R0b24lMjBmYWJyaWMlMjB0ZXh0dXJlfGVufDB8fHx8MTc3MDQ2NTQ1NXww&ixlib=rb-4.1.0&q=85"
                alt="Sustainable Fabrics"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Stats Overlay */}
            <div className="absolute -bottom-8 -right-8 md:bottom-8 md:right-8 bg-rose-700 p-6 md:p-8 shadow-2xl">
              <div className="grid grid-cols-2 gap-6">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <p className="font-serif text-2xl md:text-3xl font-medium text-white">
                      {stat.value}
                    </p>
                    <p className="font-sans text-xs text-white/70 uppercase tracking-wider mt-1">
                      {stat.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Right - Content */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
          >
            <p className="font-sans text-sm font-medium tracking-[0.3em] uppercase text-slate-400 mb-4">
              Sustainability
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-slate-900 mb-6">
              Building a<br />
              <span className="text-rose-700">Sustainable Future</span>
            </h2>
            <p className="font-sans text-lg text-slate-600 mb-10">
              As a small step towards moving to a more sustainable future, we are constantly 
              assessing and improving our processes to include eco-friendly initiatives 
              across all aspects of our manufacturing.
            </p>

            {/* Initiatives */}
            <div className="space-y-6">
              {initiatives.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="flex gap-4"
                >
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-slate-100 flex items-center justify-center">
                      <item.icon size={20} className="text-rose-700" strokeWidth={1.5} />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-serif text-lg text-slate-900 mb-1">
                      {item.title}
                    </h3>
                    <p className="font-sans text-sm text-slate-600 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default SustainabilitySection;
